/**
 * SimpleMarkdown — a small, controlled-document markdown presenter.
 *
 * WHY NOT react-markdown / marked / MDX: the inventory app ships no markdown
 * renderer dependency, and the only content this renders is the Agent API doc
 * — a known, controlled document authored in-repo. A full CommonMark parser
 * (and its transitive deps) is overkill for one trusted string. This presenter
 * handles the block grammar that doc actually uses: ATX headings, fenced code
 * blocks, pipe tables, ordered/unordered lists, blockquotes, and paragraphs,
 * with light inline formatting for `code` spans and **bold**.
 *
 * Deliberate scope limits (documented so the next person doesn't file a bug):
 *  - Tables render as PREFORMATTED text in a horizontally-scrolling <pre>, not
 *    as a real <table>. The source pipes stay; monospace keeps columns legible.
 *  - No inline links, images, or nested lists. If the Agent API doc grows to
 *    need those, promote this to a real parser (and move it to components/).
 *  - Input is TRUSTED (server-owned doc). We never render raw HTML — every node
 *    is a React element built from plain text, so there is no XSS surface even
 *    if the doc source changes.
 *
 * EXTRACTION NOTE: this is the first markdown surface in the inventory app. If
 * a second one appears, lift this to apps/frontend/src/components/ and keep one
 * copy — do not hand-roll a second presenter.
 */
import { type ReactNode } from 'react';

type Block =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'code'; content: string }
  | { kind: 'table'; content: string }
  | { kind: 'list'; ordered: boolean; items: string[] }
  | { kind: 'quote'; text: string }
  | { kind: 'paragraph'; text: string };

const FENCE = /^```/;
const HEADING = /^(#{1,6})\s+(.*)$/;
const UNORDERED = /^\s*[-*]\s+(.*)$/;
const ORDERED = /^\s*\d+\.\s+(.*)$/;
const TABLE_ROW = /^\s*\|.*\|\s*$/;
const QUOTE = /^\s*>\s?(.*)$/;

function parseBlocks(markdown: string): Block[] {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let paragraph: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ kind: 'paragraph', text: paragraph.join(' ').trim() });
      paragraph = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? '';

    // Fenced code block — consume through the closing fence.
    if (FENCE.test(line)) {
      flushParagraph();
      const content: string[] = [];
      i++;
      while (i < lines.length && !FENCE.test(lines[i] ?? '')) {
        content.push(lines[i] ?? '');
        i++;
      }
      blocks.push({ kind: 'code', content: content.join('\n') });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      flushParagraph();
      blocks.push({ kind: 'heading', level: (heading[1] ?? '').length, text: (heading[2] ?? '').trim() });
      continue;
    }

    // Pipe table — collect consecutive table rows as preformatted text.
    if (TABLE_ROW.test(line)) {
      flushParagraph();
      const rows: string[] = [];
      while (i < lines.length && TABLE_ROW.test(lines[i] ?? '')) {
        rows.push((lines[i] ?? '').trim());
        i++;
      }
      i--; // step back; outer loop re-increments
      blocks.push({ kind: 'table', content: rows.join('\n') });
      continue;
    }

    // Lists — collect consecutive matching bullets / numbers.
    if (UNORDERED.test(line) || ORDERED.test(line)) {
      flushParagraph();
      const ordered = ORDERED.test(line) && !UNORDERED.test(line);
      const items: string[] = [];
      while (i < lines.length && (UNORDERED.test(lines[i] ?? '') || ORDERED.test(lines[i] ?? ''))) {
        const cur = lines[i] ?? '';
        const m = ordered ? ORDERED.exec(cur) : UNORDERED.exec(cur);
        items.push((m?.[1] ?? '').trim());
        i++;
      }
      i--;
      blocks.push({ kind: 'list', ordered, items });
      continue;
    }

    // Blockquote — collect consecutive quoted lines.
    if (QUOTE.test(line)) {
      flushParagraph();
      const quoted: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i] ?? '')) {
        quoted.push((QUOTE.exec(lines[i] ?? '')?.[1] ?? '').trim());
        i++;
      }
      i--;
      blocks.push({ kind: 'quote', text: quoted.join(' ').trim() });
      continue;
    }

    if (line.trim() === '') {
      flushParagraph();
      continue;
    }

    paragraph.push(line.trim());
  }

  flushParagraph();
  return blocks;
}

/** Light inline formatter: `code`, **bold**, ==highlight==, *italic*. Text nodes only. */
export function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // Order matters: `code` and **bold** must match before single-star *italic*.
  const regex = /(`[^`]+`|\*\*[^*]+\*\*|==[^=]+==|\*[^*\s][^*]*\*)/g;
  let lastIndex = 0;
  let key = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith('`')) {
      nodes.push(
        <code
          key={key++}
          className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em] text-foreground"
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith('**')) {
      nodes.push(
        <strong key={key++} className="font-semibold text-foreground">
          {token.slice(2, -2)}
        </strong>,
      );
    } else if (token.startsWith('==')) {
      nodes.push(
        <mark key={key++} className="rounded bg-warning/25 px-0.5 text-foreground">
          {token.slice(2, -2)}
        </mark>,
      );
    } else {
      nodes.push(
        <em key={key++} className="italic">
          {token.slice(1, -1)}
        </em>,
      );
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

const HEADING_CLASS: Record<number, string> = {
  1: 'text-xl font-semibold tracking-tight text-foreground mt-6 first:mt-0',
  2: 'text-lg font-semibold tracking-tight text-foreground mt-6 first:mt-0',
  3: 'text-base font-semibold text-foreground mt-5 first:mt-0',
  4: 'text-sm font-semibold text-foreground mt-4 first:mt-0',
  5: 'text-sm font-medium text-foreground mt-4 first:mt-0',
  6: 'text-xs font-semibold uppercase tracking-wide text-muted-foreground mt-4 first:mt-0',
};

export function SimpleMarkdown({ markdown }: { markdown: string }) {
  const blocks = parseBlocks(markdown);

  return (
    <div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
      {blocks.map((block, index) => {
        switch (block.kind) {
          case 'heading': {
            const Tag = (`h${Math.min(block.level + 1, 6)}` as keyof JSX.IntrinsicElements);
            return (
              <Tag key={index} className={HEADING_CLASS[block.level] ?? HEADING_CLASS[3]}>
                {renderInline(block.text)}
              </Tag>
            );
          }
          case 'code':
            return (
              <div key={index} className="overflow-x-auto rounded-md border bg-muted/50">
                <pre className="min-w-full p-3">
                  <code className="font-mono text-xs leading-relaxed text-foreground">
                    {block.content}
                  </code>
                </pre>
              </div>
            );
          case 'table':
            // Preformatted — see file header for the "tables as pre" decision.
            return (
              <div key={index} className="overflow-x-auto rounded-md border bg-muted/50">
                <pre className="min-w-full p-3">
                  <code className="font-mono text-xs leading-relaxed text-foreground">
                    {block.content}
                  </code>
                </pre>
              </div>
            );
          case 'list':
            return block.ordered ? (
              <ol key={index} className="list-decimal space-y-1 pl-5 text-foreground/90">
                {block.items.map((item, i) => (
                  <li key={i}>{renderInline(item)}</li>
                ))}
              </ol>
            ) : (
              <ul key={index} className="list-disc space-y-1 pl-5 text-foreground/90">
                {block.items.map((item, i) => (
                  <li key={i}>{renderInline(item)}</li>
                ))}
              </ul>
            );
          case 'quote':
            // Deliberately kept muted (unlike list items above) — a
            // blockquote is meant to read as a visually de-emphasized aside,
            // not body copy.
            return (
              <blockquote
                key={index}
                className="border-l-2 border-border pl-3 italic text-muted-foreground"
              >
                {renderInline(block.text)}
              </blockquote>
            );
          case 'paragraph':
          default:
            return (
              <p key={index} className="text-foreground/90">
                {renderInline(block.text)}
              </p>
            );
        }
      })}
    </div>
  );
}
